<!-- 
Inspiration: https://onepagelove.com/cpg-fast-track | https://cpgfasttrack.com/?ref=onepagelove
-->

<div>
  <!-- data-nosnippet: keeps this out of Google's search result snippet -->
  <div data-nosnippet class="flex items-center justify-center gap-8 p-4 text-center text-sm text-text-secondary bg-background-light" *ngIf="showDisclaimer">
    <span class="text-start"><u>Disclaimer</u>: corpers.ng is <b>not</b> affiliated with NYSC. It's an independent platform for corp members.</span>
    <button type="button" (click)="closeDisclaimer()" aria-label="Close disclaimer" matTooltip="I understand"
      class="flex flex-shrink-0 items-center justify-center size-12 -my-3 -mr-3 rounded-lg hover:bg-black/5 active:bg-black/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary transition-colors duration-200">
      <mat-icon>close</mat-icon>
    </button>
  </div>
  <app-site-header [onHomePage]="true" [tinted]="true"></app-site-header>

  <main>

  <section class="lg:bg-[url(/assets/images/trnsprnt.grn.bg.png)] bg-no-repeat bg-right bg-accent">
    <!-- Hero Section with CPG Fast Track inspired design -->
    <div class="max-w-7xl mx-auto px-5 md:px-10 lg:px-24 py-12 md:py-16 lg:pb-24 lg:pt-12">
      <div class="max-w-4xl">
        <!-- One heading, four lines. <app-word-ticker> reads out only the showing word, with real spaces. -->
        <h1 #headline class="font-sans font-bold text-4xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tight text-primary">
          <span class="flex items-baseline flex-wrap gap-x-2 mb-4">
            <span>Buy&hairsp;</span>
            <span class="shrink-0 inline-flex items-center bg-brand-orange text-white px-3 py-1 -rotate-2 origin-bottom rounded-sm shadow-md">
              <app-word-ticker [items]="buyItems" [index]="buyWordIndex"></app-word-ticker>
            </span>
          </span>
          <span class="flex items-baseline flex-wrap gap-x-2 mb-4">
            <span>Sell your&ngsp;</span>
            <span class="shrink-0 inline-flex items-center bg-green-400 text-white px-3 py-1 rotate-1 origin-bottom rounded-sm shadow-md">
              <app-word-ticker [items]="sellItems" [index]="sellWordIndex"></app-word-ticker>
            </span>
          </span>
          <!-- Each city opens the lodges listed in its state. On small phones the city always goes on its own
               line - otherwise "PH." fits beside the text and the longer cities don't, so the page jumps. -->
          <span class="flex items-baseline flex-wrap gap-x-2 mb-4">
            <span class="max-sm:basis-full">Find a lodge in&ngsp;</span>
            <span class="shrink-0 inline-flex items-center bg-primary text-accent px-3 py-1 -rotate-1 origin-bottom rounded-sm shadow-md">
              <app-word-ticker [items]="cityItems" [index]="cityIndex"></app-word-ticker>
            </span>
          </span>
          <!-- A word's width of gap: the verb and "your" are separate flex items, and gap-x-2 is too tight at this size. -->
          <span class="flex items-baseline flex-wrap gap-x-[0.25em] mb-4">
            <app-word-ticker [items]="ppaVerbItems" [index]="ppaVerbIndex" [fixedWidth]="true"></app-word-ticker>
            <span>your&ngsp;</span>
            <a routerLink="/search" [queryParams]="{ tab: 'ppas' }" title="Search PPAs"
              class="relative inline-flex items-start group text-primary hover:text-primary-dark transition-colors">
              <span class="relative">PPA.</span>
              <mat-icon class="opacity-0 -translate-x-3 transition-all duration-200 group-hover:opacity-100 group-hover:-translate-x-4">arrow_outward</mat-icon>
              <span
                class="absolute left-0 -bottom-0.5 h-1.5 w-0 bg-primary transition-all duration-200 group-hover:w-[80%]">
              </span>
            </a>
          </span>
        </h1>
        <p class="text-lg md:text-xl text-primary/80 mt-6 mb-8 font-normal">
          Ease your NYSC year.
        </p>

        <a routerLink="/signup" class="btn-primary inline-flex items-center gap-2" title="Sign up">
          Sign up <mat-icon>arrow_forward</mat-icon>
        </a>
      </div>
    </div>
  </section>

  <section id="services" class="scroll-mt-24 section-spacing bg-background-light">
    <div class="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <h2 class="text-2xl md:text-4xl lg:text-5xl text-primary text-center mb-12 md:mb-16 font-bold">
        Here's what corp members can do on corpers.ng
      </h2>
      <div class="grid md:grid-cols-2 gap-6 md:gap-8">
        <div class="card hover:shadow-card-elevated transition-shadow duration-200 flex flex-col lg:flex-row gap-6">
          <div class="flex-shrink-0">
            <mat-icon class="text-primary">apartment</mat-icon>
          </div>
          <div>
            <h3 class="text-xl font-semibold mb-3 text-primary">
              Find Accommodation/Roommates
            </h3>
            <p class="text-text-secondary">
              Discover affordable housing options near your PPA
              (Place of Primary Assignment).
            </p>
            <p class="mt-4 text-end">
              <button class="btn-primary" (click)="openLearnMore('accommodation')">
                <span>Learn more</span>
              </button>
            </p>
          </div>
        </div>

        <div class="card hover:shadow-card-elevated transition-shadow duration-200 flex flex-col lg:flex-row gap-6">
          <div class="flex-shrink-0">
            <mat-icon class="text-primary">chat</mat-icon>
          </div>
          <div>
            <h3 class="text-xl font-semibold mb-3 text-primary">
              Chat with Corp Members
            </h3>
            <p class="text-text-secondary">
              Connect with other corp members, share experiences, and build your
              network.
            </p>
            <p class="mt-4 text-end">
              <button class="btn-primary" (click)="openLearnMore('chat')">
                <span>Learn more</span>
              </button>
            </p>
          </div>
        </div>

        <div class="card hover:shadow-card-elevated transition-shadow duration-200 flex flex-col lg:flex-row gap-6">
          <div class="flex-shrink-0">
            <mat-icon class="text-primary">sell</mat-icon>
          </div>
          <div>
            <h3 class="text-xl font-semibold mb-3 text-primary">
              Buy & Find Items for Sale
            </h3>
            <p class="text-text-secondary">
              Buy and sell essential items, from electronics to household goods,
              within the corp member community.
            </p>
            <p class="mt-4 text-end">
              <button class="btn-primary" (click)="openLearnMore('buy-and-sell')">
                <span>Learn more</span>
              </button>
            </p>
          </div>
        </div>

        <div class="card hover:shadow-card-elevated transition-shadow duration-200 flex flex-col lg:flex-row gap-6">
          <div class="flex-shrink-0">
            <mat-icon class="text-primary">domain</mat-icon>
          </div>
          <div>
            <h3 class="text-xl font-semibold mb-3 text-primary">
              Find & Review PPAs
            </h3>
            <p class="text-text-secondary">
              Locate your assigned PPA, read reviews from corps members who served there, and add yours to guide the next corper.
            </p>
            <p class="mt-4 text-end">
              <button class="btn-primary" (click)="openLearnMore('ppas')">
                <span>Learn more</span>
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Ambassador Callout -->
  <section class="bg-primary px-6 md:px-12 lg:px-24 py-10">
    <div class="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
      <div class="flex-1">
        <span class="inline-block bg-white/10 text-white/80 text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-3">Side Hustle</span>
        <h2 class="text-white font-bold text-2xl md:text-3xl leading-tight mb-2">
          Earn While You Serve. Become a corpers.ng Ambassador.
        </h2>
        <p class="text-white/65 text-sm md:text-base max-w-lg">
          Earn ₦10K per housing referral, build your CV, and help fellow corps members — all fully remote, on your own terms.
        </p>
      </div>
      <!-- The page's lime button, like the sign-up section below: lime is the accent on dark green, and
           the pill above stays neutral so this is the only accent here. -->
      <a routerLink="/ambassador" class="btn-accent flex-shrink-0 inline-flex items-center gap-2 whitespace-nowrap">
        Learn more <mat-icon class="!text-base">arrow_forward</mat-icon>
      </a>
    </div>
  </section>

  <!-- Team Section -->
  <section id="team" class="section-spacing bg-background-light">
    <div class="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <h2 class="text-2xl md:text-4xl lg:text-5xl text-primary text-center mb-4 md:mb-6 font-bold">
        Meet the team
      </h2>
      <p class="text-base md:text-lg text-text-secondary text-center mb-12 md:mb-16 max-w-2xl mx-auto">
        Former corpers building the platform we wish we had. Making your service year simpler, safer, and more meaningful.
      </p>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-12">
        <!-- Team Member Card -->
        <article 
          *ngFor="let member of teamMembers" 
          class="bg-white rounded-xl shadow-card hover:shadow-card-elevated transition-shadow duration-200 overflow-hidden"
        >
          <!-- Photo -->
          <div class="relative aspect-[4/3] w-full overflow-hidden bg-accent/20">
            <img
              [ngSrc]="member.imageUrl"
              [alt]="member.name"
              fill
              class="object-cover object-top"
            >
          </div>
          
          <!-- Content -->
          <div class="p-5">
            <!-- Info -->
            <div class="mb-3">
              <div class="flex items-center justify-between gap-2">
                <h3 class="text-xl font-semibold text-primary leading-tight">
                  {{ member.name }}
                </h3>
                <a 
                  *ngIf="member.linkedinUrl" 
                  [href]="member.linkedinUrl" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="text-[#0A66C2] hover:text-[#004182] transition-colors flex-shrink-0"
                  [attr.aria-label]="'View ' + member.name + ' on LinkedIn'"
                >
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
              <p class="text-sm font-medium text-brand-orange mt-1">
                {{ member.role }}
              </p>
            </div>
            
            <!-- Bio -->
            <p class="text-sm text-text-secondary leading-relaxed">
              {{ member.bio }}
            </p>
          </div>
        </article>
      </div>
      
      <!-- View Full Team Link - hidden for now (because all the team members are listed/fits here); /team still works.
      <div class="text-center">
        <a routerLink="/team" class="btn-primary inline-flex items-center gap-2">
          View full team
          <mat-icon>arrow_forward</mat-icon>
        </a>
      </div>
      -->
    </div>
  </section>

  <!-- Laid out like the ambassador callout: text left, button right from tablets up; stacked on phones. -->
  <section class="bg-primary text-white section-spacing">
    <div class="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row items-center justify-between gap-6 md:gap-12">
      <div class="flex-1">
        <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
          Ready to Simplify Your NYSC Journey?
        </h2>
        <p class="text-lg md:text-xl max-w-2xl text-white/90">
          Join thousands of corp members. Sign up!
        </p>
      </div>
      <a routerLink="/signup" class="btn-accent flex-shrink-0 inline-flex items-center gap-2 whitespace-nowrap" title="Sign up">
        Sign up <mat-icon>arrow_forward</mat-icon>
      </a>
    </div>
  </section>

  </main>

  <footer id="contact" class="scroll-mt-24 bg-primary-dark text-white py-10 md:py-12 px-6 md:px-12 lg:px-24">
    <div class="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      <!-- About Us -->
      <div>
        <h3 class="text-xl font-bold mb-4">About Us</h3>
        <p class="text-white/70 text-sm">
          Your reliable companion for a seamless NYSC service year in Nigeria.
        </p>
        <p class="text-white/70 text-sm mt-2">
          Meet the <a routerLink="/team" class="text-white hover:text-accent transition-colors duration-200 underline">team</a> simplifying the NYSC experience.
        </p>
      </div>

      <!-- Quick Links -->
      <div>
        <h3 class="text-lg font-semibold mb-4">Quick Links</h3>
        <ul class="space-y-2 text-white/70 text-sm">
          <li>
            <a href="#services" class="hover:text-white transition-colors duration-200">Services</a>
          </li>
          <li>
            <a routerLink="/ambassador" class="hover:text-white transition-colors duration-200">Ambassador Program</a>
          </li>
          <li>
            <a routerLink="/about" fragment="privacy-policy" class="hover:text-white transition-colors duration-200">Privacy Policy</a>
          </li>
          <li>
            <a routerLink="/about" fragment="terms" class="hover:text-white transition-colors duration-200">Terms of Service</a>
          </li>
        </ul>
      </div>

      <!-- Contact Us -->
      <div>
        <h3 class="text-lg font-semibold mb-4">Contact Us</h3>
        <ul class="space-y-3 text-white/70 text-sm">
          <li>
            <a href="mailto:contact@corpers.ng" class="inline-flex items-center gap-2 hover:text-accent transition-colors duration-200">
              <mat-icon>email</mat-icon>
              <span>contact&#64;corpers.ng</span>
            </a>
          </li>
          <li>
            <a href="tel:08163210718" class="inline-flex items-center gap-2 hover:text-accent transition-colors duration-200">
              <mat-icon>phone</mat-icon>
              <span>08163210718</span>
            </a>
          </li>
          <li>
            <a 
              href="https://wa.me/+2348163210718" 
              target="_blank" 
              rel="noopener noreferrer"
              class="inline-flex items-center gap-2 hover:text-accent transition-colors duration-200"
            >
              <!-- WhatsApp Icon -->
              <svg class="size-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span>WhatsApp</span>
            </a>
          </li>
        </ul>
      </div>

      <!-- Office Address -->
      <div>
        <h3 class="text-lg font-semibold mb-4">Office Address</h3>
        <address class="text-white/70 text-sm not-italic space-y-1">
          <p>48 Ndidem Usang Iso Rd, Calabar</p>
          <p>(2nd floor, inside HIBM)</p>
          <p>Cross River State, Nigeria</p>
        </address>
      </div>
    </div>

    <!-- Social Media -->
     <!-- @todo: update social media links -->
    <div class="max-w-7xl mx-auto mt-8 pt-6 border-t border-white/20">
      <div class="flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h4 class="text-sm font-semibold mb-3 md:mb-0 text-center md:text-left">Follow us on social media</h4>
        </div>
        <div class="flex items-center gap-4">
          <!-- LinkedIn -->
          <a 
            href="https://linkedin.com/company/corpers-ng"
            target="_blank" 
            rel="noopener noreferrer"
            class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent hover:text-primary transition-all duration-200"
            aria-label="Follow us on LinkedIn"
          >
            <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </a>
          <!-- Instagram -->
          <a 
            href="https://www.instagram.com/corpers.ng"
            target="_blank" 
            rel="noopener noreferrer"
            class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent hover:text-primary transition-all duration-200"
            aria-label="Follow us on Instagram"
          >
            <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
            </svg>
          </a>
          <!-- X (Twitter) -->
          <a 
            href="https://x.com/Corpers_NG"
            target="_blank" 
            rel="noopener noreferrer"
            class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent hover:text-primary transition-all duration-200"
            aria-label="Follow us on X"
          >
            <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/>
            </svg>
          </a>
          <!-- Facebook -->
          <!-- <a 
            href="https://facebook.com/_" 
            target="_blank" 
            rel="noopener noreferrer"
            class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent hover:text-primary transition-all duration-200"
            aria-label="Follow us on Facebook"
          >
            <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </a> -->
          <!-- TikTok -->
          <a 
            href="https://tiktok.com/@corpers.ng"
            target="_blank" 
            rel="noopener noreferrer"
            class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent hover:text-primary transition-all duration-200"
            aria-label="Follow us on TikTok"
          >
            <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
            </svg>
          </a>
        </div>
      </div>
    </div>

    <!-- Copyright -->
    <div class="max-w-7xl mx-auto text-center text-white/60 text-sm mt-6 pt-6 border-t border-white/20">
      <p>&copy; {{fullYear}} corpers.ng Limited &middot; RC 8064066 &middot; All rights reserved</p>
    </div>
  </footer>
</div>